import classNames from "classnames/bind";
import {
  AddItemIcon,
  AskQuestionIcon,
  DangerIcon,
  FeedbackIcon,
  HomeIcon,
  ProfileIcon,
  SyllabusIcon,
} from "common/assets/icons/";
import { DragIcon } from "common/assets/icons/DragIcon";
import { SignoutIcon } from "common/assets/icons/SignoutIcon";
import { SimulationIcon } from "common/assets/icons/SimulationIcon";
import { AuthData, useAuth } from "common/auth/Auth";
import { IconButton, LogoutButton } from "common/buttons";
import {
  ADMIN_PREFIX,
  ARTIKKEL_PREFIX,
  KURSSLOEYFE_PREFIX,
  MAPPE_PREFIX,
  OPPGAVE_PREFIX,
  PENSUM_PREFIX,
  SELVTEST_PREFIX,
  STUDIEPROGRAM_PREFIX,
  urls,
} from "common/urls";
import React, { FC, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import styles from "./NavBar.module.css";

const classNameBuilder = classNames.bind(styles);

enum MenuLocation {
  TOP,
  BOTTOM,
}

export type NavBarItem = {
  icon: React.ReactNode;
  text: string;
  menuLocation: MenuLocation;
};

export interface NavBarLinkItem extends NavBarItem {
  path: string;
}

export interface NavBarChildItem extends NavBarItem {
  children: FC<{ isMenuOpen: boolean }>;
}

const isNavBarLinkItem = (value: NavBarItem): value is NavBarLinkItem =>
  "path" in value;
const isNavBarButtonItem = (value: NavBarItem): value is NavBarChildItem =>
  "children" in value;

function buildMenuItems(props: { auth: AuthData }): NavBarItem[] {
  const { auth } = props;
  const grants = {
    admin: auth.me?.menu.admin ?? false,
    feedback: auth.me?.menu.feedback ?? false,
    questions: auth.me?.menu.feedback ?? false,
    graphiql: auth.me?.menu.graphiql ?? false,
    studies: auth.me?.menu.studies ?? false,
    teacherFeedback: auth.me?.menu.teacherFeedback ?? false,
    teacherQuestions: auth.me?.menu.teacherFeedback ?? false,
  };

  return [
    {
      path: urls.frontPage,
      icon: <HomeIcon />,
      text: "Hjem",
      menuLocation: MenuLocation.TOP,
      show: true,
    },
    {
      path: urls.myStudies,
      icon: <SyllabusIcon size={32} />,
      text: "Mitt pensum",
      menuLocation: MenuLocation.TOP,
      show: grants.studies,
    },
    {
      path: urls.simulation,
      icon: <SimulationIcon size={32} />,
      text: "Simulering",
      menuLocation: MenuLocation.TOP,
      show: grants.studies,
    },
    {
      path: urls.questions,
      icon: <AskQuestionIcon />,
      text: "Spørsmål",
      menuLocation: MenuLocation.TOP,
      show: grants.questions,
    },
    {
      path: urls.feedback,
      icon: <FeedbackIcon />,
      text: "Tilbakemelding",
      menuLocation: MenuLocation.TOP,
      show: grants.feedback,
    },
    {
      path: urls.teacherQuestions,
      icon: <AskQuestionIcon />,
      text: "Innboks",
      menuLocation: MenuLocation.BOTTOM,
      show: grants.teacherQuestions,
    },
    {
      path: urls.teacherFeedback,
      icon: <FeedbackIcon />,
      text: "Tilbakemeldinger",
      menuLocation: MenuLocation.BOTTOM,
      show: grants.teacherFeedback,
    },
    {
      path: urls.admin,
      icon: <AddItemIcon />,
      text: "Administrasjon",
      menuLocation: MenuLocation.BOTTOM,
      show: grants.admin,
    },
    {
      path: urls.graphiql,
      icon: <DangerIcon />,
      text: "GraphiQL",
      menuLocation: MenuLocation.BOTTOM,
      show: grants.graphiql,
    },
    {
      path: urls.profileStudies,
      icon: <ProfileIcon />,
      text: auth.me?.principal?.user.fullName ?? "Profil",
      menuLocation: MenuLocation.BOTTOM,
      show: auth.isLoggedIn,
    },
    {
      children: ({ isMenuOpen }: { isMenuOpen: boolean }) => (
        <>
          <LogoutButton>
            <button
              className={`${styles.resetButton}${
                isMenuOpen ? ` ${styles.navLink}` : ""
              }`}
            >
              <span className={styles.linkIcon}>
                <SignoutIcon />
              </span>
              <span className={styles.linkText}>{"Logg ut"}</span>
            </button>
          </LogoutButton>
        </>
      ),
      icon: <SignoutIcon />,
      text: "Logg ut",
      menuLocation: MenuLocation.BOTTOM,
      show: auth.isLoggedIn && auth.me != null,
    },
  ].filter((it) => it.show);
}

interface Props {
  isMenuOpen: boolean;
  handleOnMenuClick: React.Dispatch<React.SetStateAction<boolean>>;
}

export const NavBar: React.FC<React.PropsWithChildren<Props>> = ({
  isMenuOpen,
  handleOnMenuClick,
}) => {
  const auth = useAuth();
  const location = useLocation();
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        handleOnMenuClick(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isMenuOpen, handleOnMenuClick]);

  const navBarStyles = classNameBuilder({
    navBar: true,
    navBarOpen: isMenuOpen,
  });

  const navActiveStyles = classNameBuilder({
    navActive: true,
    navActiveOpen: isMenuOpen,
  });

  const contentStyles = classNameBuilder({
    desktop: true,
  });

  const menuItems = buildMenuItems({ auth });

  const buildNavLinks = (items: NavBarItem[], predicate: MenuLocation) => {
    return items
      .filter((it) => it.menuLocation === predicate)
      .map((it) => {
        if (isNavBarLinkItem(it)) {
          const isActiveFn = () => {
            return (
              location.pathname === it.path ||
              (it.path === urls.myStudies &&
                [
                  `/${STUDIEPROGRAM_PREFIX}`,
                  `/${PENSUM_PREFIX}`,
                  `/${MAPPE_PREFIX}`,
                  `/${KURSSLOEYFE_PREFIX}`,
                  `/${ARTIKKEL_PREFIX}`,
                  `/${OPPGAVE_PREFIX}`,
                  `/${SELVTEST_PREFIX}`,
                ].some((e) => location.pathname.startsWith(e))) ||
              (it.path === urls.admin &&
                location.pathname.startsWith(`/${ADMIN_PREFIX}`))
            );
          };

          return (
            <NavLink
              key={it.path}
              to={it.path}
              end
              onClick={() => handleOnMenuClick(false)}
              className={({ isActive }) =>
                `${styles.navLink} ${isActive || isActiveFn() ? navActiveStyles : ""}`
              }
            >
              <span className={styles.linkIcon}>{it.icon}</span>
              <span className={styles.linkText}>{it.text}</span>
            </NavLink>
          );
        } else if (isNavBarButtonItem(it)) {
          return (
            <React.Fragment key={it.text}>
              <div onClick={() => handleOnMenuClick(false)}>
                {it.children({ isMenuOpen })}
              </div>
            </React.Fragment>
          );
        }

        return null;
      });
  };

  return (
    <>
      <IconButton
        onClick={() => handleOnMenuClick((prev) => !prev)}
        icon={<DragIcon />}
        altText={isMenuOpen ? "Lukk meny" : "Åpne meny"}
        className={styles.menuIconButtonMobile}
      />
      <section className={navBarStyles} ref={navRef}>
        <IconButton
          onClick={() => handleOnMenuClick((prev) => !prev)}
          icon={<DragIcon />}
          altText={isMenuOpen ? "Lukk meny" : "Åpne meny"}
          className={styles.menuIconButtonDesktop}
        />
        <div className={contentStyles}>
          <div className={styles.topNavContainer}>
            {buildNavLinks(menuItems, MenuLocation.TOP)}
          </div>
          <div className={styles.bottomNavContainer}>
            {buildNavLinks(menuItems, MenuLocation.BOTTOM)}
          </div>
        </div>
      </section>
    </>
  );
};
